// Контракт ВЕБ-ВКЛАДА модуля — то, что модуль даёт интерфейсу веб-приложения.
//
// ┌─ Почему это отдельный контракт, а не поля в `ModuleManifest` ──────────────┐
// │ `@studyhub/shared/module` описывает СПОСОБНОСТИ модуля (kind, lifecycle,   │
// │ learning.role, вклады в 13 сквозных реестров) и запрещает у себя поля      │
// │ расположения в интерфейсе (декрет §4.2). Иконка, i18n-ключ, deep-link и    │
// │ цвет плитки — это не способности, а вклад в конкретный клиент; на сервере  │
// │ их не существует, а в веб-бандл серверные манифесты не попадают            │
// │ (`packages/shared/src/module/index.ts`, шапка файла).                      │
// │                                                                            │
// │ Поэтому здесь ровно два вида полей:                                        │
// │   • факты самого модуля  — иконка, ключ названия, свой роут, свой SQL      │
// │     счётчика, свой deep-link;                                              │
// │   • ЧИСЛА ПОРЯДКА       — `order` внутри поверхности.                      │
// │                                                                            │
// │ Группу («первичная навигация» / «Инструменты» / «системная») выбирает      │
// │ ПОТРЕБИТЕЛЬ (`nav-config.ts`), а не манифест: смена IA обязана быть        │
// │ правкой одного потребителя, а не двадцати манифестов. `order` оставлен     │
// │ намеренно — без него порядок держался бы на позиции статического импорта   │
// │ в реестре, а это худший из возможных источников порядка. Дефолт (1000)     │
// │ ставит НОВЫЙ модуль в конец своей группы: он появляется сам, но не         │
// │ перетасовывает существующую навигацию.                                     │
// └────────────────────────────────────────────────────────────────────────────┘

import type { TopicKitKind } from "@studyhub/shared/contracts/courses";
import type { ModuleId } from "@studyhub/shared/module";
import type { Category } from "@studyhub/ui/components/studyhub/category";
import type { TintColor } from "@studyhub/ui/components/studyhub/tint-tile";
import type { Icon } from "@tabler/icons-react";

/** Позиция по умолчанию — конец списка. Новый модуль не двигает старые. */
export const DEFAULT_SURFACE_ORDER = 1000;

// ─── Счётчик пункта навигации ───────────────────────────────────────────────

/**
 * Откуда пункт берёт число рядом с названием.
 *
 * - `powersync` — скалярный `SELECT count(*) …`; каждый `?` подставляется как
 *   `user_id` текущего пользователя. Такой счётчик работает офлайн, реактивен и
 *   НЕ требует правки `use-nav-badges.ts` — именно он закрывает бывший скрытый
 *   реестр из 13 записей по URL-строке.
 * - `client` — величина считается React-хуком (серверные данные без синка,
 *   например `visual_analyses`). Такой модуль обязан быть в карте
 *   `CLIENT_NAV_BADGES`: хук нельзя вызвать из данных, его вызывает код.
 */
export type NavBadgeSource =
	| { readonly kind: "powersync"; readonly sql: string }
	| { readonly kind: "client" };

/** Пункт навигации, который даёт модуль. Модуль может дать их несколько. */
export interface WebNavEntry {
	/** Стабильный ключ пункта — ключ бейджа, тестов и выбора группы потребителем. */
	readonly key: string;
	/** Целевой путь (он же ключ подсветки и совместимости с `navBadges[item.to]`). */
	readonly to: string;
	readonly icon: Icon;
	/** Ключ i18n в namespace `layout` (например `nav.notes`). */
	readonly labelKey: string;
	/** Короткая подпись для тесного BottomNav. */
	readonly shortLabelKey?: string;
	/**
	 * Дополнительные префиксы активности: легаси-адреса и detail-страницы
	 * раздела, живущие под другим URL (`/library` ↔ `/materials/$id`).
	 */
	readonly activePrefixes?: readonly string[];
	readonly badge?: NavBadgeSource;
	/** Янтарный тон счётчика — приоритетный раздел. */
	readonly badgeTone?: "warning";
	/** Акцент-маркер справа от пункта (`✦`). */
	readonly accent?: "sparkle";
	/** Позиция внутри своей группы. */
	readonly order?: number;
}

// ─── Плитка витрины возможностей (дашборд) ──────────────────────────────────

/**
 * Плитка лаунчера. Ведёт СРАЗУ на экран создания/старта (новый чат, мастер
 * записи, диалог эссе), а не на список раздела.
 *
 * Либо `to` (навигация), либо `action` (действие исполняет клиентский слой —
 * поведение из данных не сериализуется, см. `WebWorkspaceTool`).
 */
export interface WebLaunchTile {
	readonly key: string;
	readonly icon: Icon;
	readonly tint: TintColor;
	/** Ключи i18n в namespace `dashboard`. */
	readonly titleKey: string;
	readonly hintKey: string;
	readonly to?: string;
	readonly params?: Readonly<Record<string, string>>;
	readonly search?: Readonly<Record<string, unknown>>;
	/** Идентификатор действия для клиентского слоя (плитка-кнопка, не ссылка). */
	readonly action?: WebLaunchAction;
	/** Акцентная плитка — лёгкий primary-бордер. */
	readonly accent?: boolean;
	readonly order?: number;
}

/** Действия лаунчера, реализуемые клиентским слоем `feature-launcher.tsx`. */
export type WebLaunchAction = "upload" | "discover";

// ─── Плитка «Инструменты» воркспейса курса ──────────────────────────────────

/** Шаг спины темы: ① разобраться → ② проверить себя → ③ добрать. */
export type CourseToolStep = 1 | 2 | 3;

/**
 * Канал восприятия, которым плитка даёт тему.
 *
 * Это ФАКТ САМОГО МОДУЛЯ, а не место в интерфейсе: «инфографика — то, на что
 * смотрят» верно независимо от того, группирует панель плитки или нет. Поэтому
 * канал живёт в манифесте, а не картой «ключ → группа» внутри панели: такая
 * карта стала бы вторым, скрытым реестром — ровно тем, ради устранения чего
 * реестр и вводили (декрет §4.2). Новый модуль попадает в свою группу сам,
 * панель о его существовании не знает.
 *
 * - `read` — читают глазами (термины, выжимка, заметка, конспект);
 * - `watch` — рассматривают целиком (схема, инфографика);
 * - `listen` — слушают (аудиоформат);
 * - `practice` — отвечают и говорят сами (карточки, тест, диалог, голос);
 * - `plan` — служебное: не способ воспринять тему, а работа с расписанием.
 *   Пятое значение заведено намеренно, вместо того чтобы затолкать календарь в
 *   `practice`: заголовок группы обязан быть правдой, иначе группировка теряет
 *   смысл ровно там, где человек ей поверил.
 */
export type ToolChannel = "read" | "watch" | "listen" | "practice" | "plan";

/**
 * Вклад модуля в панель «Инструменты» воркспейса.
 *
 * СОСТАВ, порядок, иконка и канал восприятия приходят отсюда; ПОВЕДЕНИЕ
 * (`run` / `isBusy` / `isDisabled`) — замыкания на `CourseToolContext`, их
 * нельзя положить в данные и не нужно: клиентский слой
 * `workspace/tools/tool-behavior.tsx` держит карту «ключ → поведение».
 *
 * Модуль, объявивший `kitKind` и не имеющий записи в карте поведения, получает
 * ОСМЫСЛЕННЫЙ дефолт — «добрать этот вид комплекта темы». Это и делает
 * контрольный DoD настоящим: новый формат даёт рабочую плитку без правки панели.
 */
export interface WebWorkspaceTool {
	readonly key: string;
	/**
	 * Канал восприятия — группа плитки в панели. Поле ОБЯЗАТЕЛЬНОЕ: иначе новая
	 * плитка молча оседала бы в «прочем», а именно молчаливое выпадение из
	 * группы и есть тот дефект, который группировка должна была убрать.
	 *
	 * Цвет плитки отсюда НЕ берётся: он один на всю группу (см.
	 * `workspace/tools/tool-channel-groups.tsx`). Прежнее поле `category`
	 * убрано вместе с разноцветностью внутри группы — держать поле, которое
	 * больше ни на что не влияет, значит оставить ловушку следующему автору.
	 */
	readonly channel: ToolChannel;
	readonly icon: Icon;
	/** Плитка требует сети (серверная генерация) — офлайн гасится с объяснением. */
	readonly requiresOnline?: boolean;
	/**
	 * В каких шагах плитка показывается и на каком месте.
	 *
	 * Карта, а не скаляр: продуктовые порядки шагов РАЗНЫЕ (репетитор стоит
	 * предпоследним на шаге ② и вторым на шаге ③), одним числом их не выразить.
	 */
	readonly steps: Partial<Record<CourseToolStep, number>>;
	/** Вид комплекта темы, который добирает плитка — вход дефолтного поведения. */
	readonly kitKind?: TopicKitKind;
}

// ─── Баннер-объяснение раздела ──────────────────────────────────────────────

/**
 * Текст баннера «Что это за раздел» (`SectionIntro`, план §8.1-8.2).
 *
 * Живёт в манифесте, а не в компоненте страницы, по той же причине, что и всё
 * остальное здесь: модуль, заведённый через `/init-module`, обязан получать
 * место под своё объяснение целиком — включая строки. Список баннеров,
 * захардкоженный на страницах, вернул бы правку сквозного файла при каждом
 * новом модуле (инвариант плана §10.2).
 *
 * Ключи ПОЛНЫЕ, с namespace своего модуля (`"weak-spots:intro.title"`), а не
 * относительные к общему словарю: общий неймспейс `intro.*` — тот же сквозной
 * файл, только под другим именем (`docs/copy/ONBOARDING-COPY.md` §0).
 */
export interface WebSectionIntro {
	/** Заголовок «Что это» — короткий вопрос-утверждение о разделе. */
	readonly titleKey: string;
	/** Одно-два предложения: что это и откуда берётся. */
	readonly bodyKey: string;
	/**
	 * Пункты «что здесь можно» — 2-3 ключа.
	 *
	 * Список ключей, а не один ключ-массив: манифест обязан быть читаемым
	 * глазами (видно, сколько пунктов объявлено), а гейт переводов — уметь
	 * проверить каждый пункт отдельно.
	 */
	readonly bulletKeys: readonly string[];
}

// ─── Манифест ───────────────────────────────────────────────────────────────

/** Веб-вклад одного модуля. Лежит рядом с реестром, подключается статически. */
export interface WebModuleManifest {
	readonly id: ModuleId;
	/** Каноническая иконка-эмблема раздела (`SECTION_ICONS`). */
	readonly icon: Icon;
	/** Ключ названия раздела в namespace `layout`. */
	readonly labelKey: string;
	/** Цвет-эмблема плиток раздела (`SECTION_TINT`). */
	readonly tint?: TintColor;
	/** Категория «Картотеки» (`SECTION_CATEGORY`). */
	readonly category?: Category;

	/**
	 * Префиксы роутов, принадлежащих модулю в вебе.
	 *
	 * ⚠️ Это НЕ копия `contributes.routes` серверного манифеста: там список для
	 * серверного Route Guard, здесь — реальные пути SPA. Расхождения между ними
	 * реальны и обязаны находиться гейтом, а не молча уживаться
	 * (`module-registry-parity.test.ts`).
	 */
	readonly routes: readonly string[];
	/** Префиксы, на которых приложение рисует overlay-каркас (без сайдбара). */
	readonly overlayShellPrefixes?: readonly string[];
	/** Префиксы, где глобальная панель репетитора не монтируется. */
	readonly tutorSuppressedPrefixes?: readonly string[];

	readonly nav?: readonly WebNavEntry[];
	readonly launch?: readonly WebLaunchTile[];
	readonly workspaceTools?: readonly WebWorkspaceTool[];
	/**
	 * Баннер-объяснение над списком раздела. Необязателен: раздел, чьё название
	 * говорит само за себя, объяснения не заводит (план §8.3, волна 3).
	 */
	readonly intro?: WebSectionIntro;
}
